import get from 'lodash.get';

export default function consentFactory(services) {
  const thisService = {
    init,
    isConsentLoadedAsync,
    isPerformanceEnabled,
    isGroupEnabled,
    needUpdate,
    isReprompt,
    getConsent,
    setConsentAsync,
    getLatestPurpose,
  };

  let XRegExp = require('xregexp');
  let PERF_EXP;
  let performanceEnabled;
  let consentLoaded = false;
  let lastActiveGroups = null;
  let intervalId = null;
  return thisService;

  //////////////
  function init() {
    PERF_EXP = new XRegExp(',1,');
    loadConsentScript();
  }

  function isConsentLoadedAsync() {
    return new Promise((resolve) => {
      if (consentLoaded) {
        resolve();
      }
      else {
        services.window.addEventListener('posb-consent-ready', () => {
          resolve();
        });
      }

    });
  }

  function isPerformanceEnabled() {
    return isGroupEnabled(2);
  }

  function checkCookies() {
    let test = isPerformanceEnabled();
    if (test !== performanceEnabled) {
      performanceEnabled = test;
      services.ga.eventTrack('consent', performanceEnabled ? 'enabled' : 'disabled');
      services.ga.setEnable(performanceEnabled);
    }

    services.window.setTimeout(checkCookies, 10000);
  }

  function loadConsentScript() {
    const clientConfig = services.session.getClientConfig();
    let consentScript = get(clientConfig, 'consent.consentScript');
    if (consentScript) {
      let consentScriptElement = services.document.createElement('script');
      consentScriptElement.src = consentScript;
      // consentScriptElement.setAttribute('type', 'text/javascript');
      // consentScriptElement.setAttribute('charset', 'UTF-8');

      checkForOptanonReady();
      services.document.head.appendChild(consentScriptElement);
    }
    else {
      consentLoaded = true;
      services.window.dispatchEvent(new CustomEvent('posb-consent-ready', {detail: {script: false}}));
    }
  }

  function loadHotJarScript(loadHotJar = false) {
    if (loadHotJar) {
      console.log('Loading HotJar script');
      let hotJarScriptElement = services.document.createElement('script');
      hotJarScriptElement.src = 'https://static-dev.tropos-rnd.com/largeformat/cloud-front-utils/latest/hotjar/hotjar.js';
      hotJarScriptElement.setAttribute('type', 'text/javascript');
      hotJarScriptElement.setAttribute('charset', 'UTF-8');

      services.document.head.appendChild(hotJarScriptElement);
      checkForHotJarReady();
    }
  }

  function checkForHotJarReady() {
    if (!services.window.includeHotjar) {
      services.window.setTimeout(checkForHotJarReady, 500);
      return;
    }

    services.window.includeHotjar(services.window.location.hostname.includes('www.printos.com'));
  }

  function checkForOptanonReady() {
    if (!services.window.Optanon) {
      services.window.setTimeout(checkForOptanonReady, 500);
      return;
    }

    handleOptanonReady();
  }

  function handleOptanonReady() {
    // services.window.Optanon.LoadBanner();
    consentLoaded = true;
    services.window.dispatchEvent(new CustomEvent('posb-consent-ready', {detail: {script: true}}));
    checkForOptanonConfigured();
  }

  function checkForOptanonConfigured() {
    let test = services.preferences.getCookie('OptanonAlertBoxClosed');
    if (!test) {
      setTimeout(checkForOptanonConfigured, 50);
    }
    else {
      checkCookies();
      lastActiveGroups = window._O.getOptInGroups();
      services.ga.init(isPerformanceEnabled() || services.session.isDevice());
      // For quick and dirty solution, we will load the HotJar script on header load if performance is enabled
      loadHotJarScript(isPerformanceEnabled() && services.session.isHotJarEnabled());
      services.window.dispatchEvent(new CustomEvent('posb-consent-configured', {detail: {gaEnabled: isPerformanceEnabled()}}));
      intervalId = setInterval(watchForOptanonChanged,1000);
    }
  }

  function watchForOptanonChanged() {
    // let test = window._O.getOptInGroups();
    let test = services.preferences.getCookie('OptanonConsent', 'groups');
    if (test !== lastActiveGroups) {
      lastActiveGroups = test;
      services.ga.setEnable(isPerformanceEnabled());
      services.window.dispatchEvent(new CustomEvent('posb-consent-changed', {detail: {gaEnabled: isPerformanceEnabled()}}));
    }
  }

  function isGroupEnabled(group) {
    let groupOptions = services.preferences.getCookie('OptanonConsent', 'groups');
    if (!groupOptions) {
      return false;
    }

    let options = {};
    groupOptions.split(',').forEach(go => {
      let split = go.split(':');
      options[split[0]] = split[1];
    });

    return options[group] ? options[group] === '1' : false;
  }

  function needUpdate(purposeId) {
    let cc = services.session.getClientConfig();
    if (!cc.consent?.userConsents ) {
      return false;
    }

    if (!cc.consent?.userConsents) {
      return false;
    }

    let userConsent = cc.consent.userConsents.find(uc => { return uc.purposeUid === purposeId });
    if (!userConsent || userConsent.purposeUid !== purposeId) {
      return false;
    }

    return !!userConsent.shouldPromptForConsent;
  }

  function isReprompt(purposeId) {
    let cc = services.session.getClientConfig();
    if (!cc.consent?.userConsents ) {
      return false;
    }

    if (!cc.consent?.userConsents) {
      return false;
    }

    let userConsent = cc.consent.userConsents.find(uc => { return uc.purposeUid === purposeId });
    if (!userConsent || userConsent.purposeUid !== purposeId) {
      return false;
    }

    return !!userConsent.reprompting;
  }

  function getLatestPurpose(purposeId) {
    let cc = services.session.getClientConfig();
    let locale = services.i18n.getShortLocale();

    if (!cc.consent?.userConsents) {
      return null;
    }

    let userConsent = cc.consent.userConsents.find(uc => { return uc.purposeUid === purposeId });
    if (!userConsent || userConsent.purposeUid !== purposeId) {
      return null;
    }

    return userConsent.purposes.records.find(p => { return p.verbiage.locale === locale });
  }

  function getConsent(purposeId) {
    let cc = services.session.getClientConfig();

    if (!cc.consent?.userConsents) {
      return null;
    }

    let userConsent = cc.consent.userConsents.find(uc => { return uc.purposeUid === purposeId });
    if (!userConsent || userConsent.purposeUid !== purposeId) {
      return null;
    }

    return userConsent.userConsentRecord;
  }

  function setConsentAsync(payload) {
    return services.http.put('consent_service', 'consent', payload, {version: 1}).then(() => {
      services.session.getClientConfigAsync(true);
    });
  }
}
